import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../hooks/useAuth';
import { useDima } from '../hooks/useDima';
import { useModal } from '../hooks/useModal';
import { LoginModal } from './LoginModal';
import './MainPage.css';

export const MainPage = () => {
  const { currentUser } = useUser();
  const loginModal = useModal();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  const auth = useAuth();
  const dima = useDima();

  // Загружаем статус DIMA при монтировании и при изменении пользователя
  useEffect(() => {
    if (currentUser) {
      dima.fetchStatus().catch(err => {
        console.error('Failed to fetch DIMA status:', err);
      });
    }
  }, [currentUser, dima]);

  const handleLogin = async (email, password) => {
    await auth.login(email, password);
  };

  const handleConfirmEmail = async (code, email) => {
    await auth.confirmEmail(code, email);
  };

  const handleResetPassword = async (email) => {
    await auth.resetPassword(email);
  };

  const handleConfirmResetPassword = async (code, email) => {
    await auth.confirmResetPassword(code, email);
  };

  const handleSetNewPassword = async (password) => {
    await auth.setNewPassword(password);
  };

  const handleConnect = async () => {
    if (!currentUser) {
      loginModal.open();
      return;
    }

    setIsConnecting(true);
    setConnectionError(null);

    try {
      // Определяем новый статус (включаем, если выключен, и наоборот)
      const newStatus = !dima.isEnabled;
      
      await dima.setEnabled(newStatus);
      
    } catch (err) {
      setConnectionError(err.message);
      console.error('Connection error:', err);
    } finally {
      setIsConnecting(false);
    }
  };

  // Получаем текст кнопки в зависимости от статуса
  const getConnectButtonText = () => {
    if (isConnecting) return 'Подключение...';
    if (dima.isEnabled === true) return 'Отключить';
    if (dima.isEnabled === false) return 'Подключить';
    return 'Подключить';
  };

  return (
    <div className="main-page">
      <header className="header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo">
              <span className="logo-text">DimaAI</span>
            </div>
            <nav className="nav">
              <a href="#features" className="nav-link">Зачем</a>
              <button 
                className="nav-link nav-connect-btn"
                onClick={handleConnect}
                disabled={isConnecting || dima.isLoading}
              >
                {getConnectButtonText()}
              </button>
            </nav>
          </div>
          <div className="header-right">
            {currentUser ? (
              <div className="user-avatar">
                {currentUser.profile_image_url ? (
                  <img 
                    src={currentUser.profile_image_url} 
                    alt={currentUser.login || currentUser.email}
                    className="avatar-image"
                  />
                ) : (
                  <div className="avatar-placeholder">
                    {(currentUser.login || currentUser.email || 'U')[0].toUpperCase()}
                  </div>
                )}
              </div>
            ) : (
              <button className="btn btn-primary login-btn" onClick={loginModal.open}>
                Войти
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="main-content">
        <section className="hero">
          <div className="hero-content">
            <h1 className="hero-title">
              {dima.isEnabled ? (
                <>DimaAI <span className="gradient-text">активен</span> на вашем канале</>
              ) : (
                <>Подключите <span className="gradient-text">DimaAI</span> к вашему каналу</>
              )}
            </h1>
            <p className="hero-subtitle">
              {dima.isEnabled 
                ? 'Интеллектуальный ассистент работает и готов отвечать на вопросы зрителей в реальном времени.'
                : 'Интеллектуальный ассистент для вашего стрима. Отвечает на вопросы зрителей и создаёт уникальный контент в реальном времени.'
              }
            </p>
            
            {/* Индикатор загрузки статуса */}
            {dima.isLoading && currentUser && (
              <p className="hero-loading">Загрузка статуса DimaAI...</p>
            )}

            {/* Статус подключения */}
            {!dima.isLoading && currentUser && dima.isEnabled !== null && (
              <div className={`connection-status ${dima.isEnabled ? 'connected' : 'disconnected'}`}>
                <span className="status-dot"></span>
                {dima.isEnabled ? 'DimaAI подключен' : 'DimaAI отключен'}
              </div>
            )}

            {/* Ошибка соединения */}
            {connectionError && (
              <div className="connection-error">
                ⚠️ {connectionError}
              </div>
            )}

            <button 
              className={`btn ${dima.isEnabled ? 'btn-secondary' : 'btn-primary'} hero-btn`}
              onClick={handleConnect}
              disabled={isConnecting || dima.isLoading}
            >
              {isConnecting ? 'Подключение...' : getConnectButtonText()}
            </button>
            
            {!currentUser && (
              <p className="hero-hint">
                🔒 Для подключения необходимо <button className="link-btn" onClick={loginModal.open}>войти</button>
              </p>
            )}
          </div>
        </section>

        <section id="features" className="features">
          <h2 className="features-title">Зачем Вам DimaAI?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">💬</div>
              <h3>Интерактивный чат</h3>
              <p>Отвечает на вопросы зрителей, поддерживает диалог и создаёт живое общение в вашем чате.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">👾</div>
              <h3>Контент в реальном времени</h3>
              <p>Генерирует идеи для стрима, подсказывает темы и помогает вовлекать зрителей.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Мгновенная интеграция</h3>
              <p>Подключается за секунду. Не требует дополнительных настроек и технических знаний.</p>
            </div>
          </div>
        </section>

      </main>

      <footer className="footer">
        <div className="footer-container">
          <span className="footer-copyright">
            © 2026 Пантеон. Все права защищены.
          </span>
          <a 
            href="https://t.me/pantheonsocial" 
            target="_blank" 
            rel="noopener noreferrer"
            className="footer-telegram"
            aria-label="Telegram"
          >
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M12 0C5.373 0 0 5.373 0 12C0 18.627 5.373 24 12 24C18.627 24 24 18.627 24 12C24 5.373 18.627 0 12 0ZM17.562 8.162L15.786 16.025C15.643 16.651 15.279 16.798 14.755 16.501L11.802 14.275L10.368 15.646C10.212 15.802 10.083 15.932 9.789 15.932L9.993 12.927L15.498 7.948C15.742 7.729 15.447 7.605 15.126 7.824L8.276 12.213L5.376 11.212C4.762 11.009 4.749 10.585 5.508 10.288L16.803 5.934C17.339 5.731 17.818 6.065 17.562 8.162Z" 
                fill="currentColor"
              />
            </svg>
          </a>
        </div>
      </footer>

      <LoginModal
        isOpen={loginModal.isOpen}
        onClose={loginModal.close}
        onLogin={handleLogin}
        onConfirmEmail={handleConfirmEmail}
        onCancelConfirmation={auth.cancelConfirmation}
        onResetPassword={handleResetPassword}
        onConfirmResetPassword={handleConfirmResetPassword}
        onSetNewPassword={handleSetNewPassword}
        onCancelResetPassword={auth.cancelResetPassword}
        onStartResetPasswordFlow={auth.startResetPasswordFlow}
        isLoading={auth.isLoading}
        error={auth.error}
        needsConfirmation={auth.needsConfirmation}
        pendingEmail={auth.pendingEmail}
        resetStage={auth.resetStage}
        resetEmail={auth.resetEmail}
      />
    </div>
  );
};